// The DS §2.1 states of a Step 8/9 card before its read has data: while the read loads, the card's title over the D0
// skeleton; on a system failure (a 5xx or a lost connection), the card's title over the D0 error strip with Retry. A
// refusal is not a failure: the server said this seat may not read the record, so the card is not drawn at all (the
// step's gate sentence belongs to the page that refuses, never to an error strip).

import type { ComponentProps } from "react";
import type { AppError } from "@/api/runtime";
import { isClientRefusal } from "@/api/useTool";
import { Card, RULE_LABELS, type Hs } from "@/mc";

export type CardReadState = "hidden" | "loading" | "failed" | "ready";

/** Where a card stands on its read: data wins, a refusal hides it, a system failure shows the strip. */
export function cardReadState(read: { data?: unknown; error?: AppError | null | undefined; isLoading: boolean }): CardReadState {
  if (read.error && isClientRefusal(read.error)) return "hidden";
  if (read.data !== undefined && read.data !== null) return "ready";
  if (read.error) return "failed";
  return read.isLoading ? "loading" : "hidden";
}

export interface CardReadSlotProps {
  state: CardReadState;
  title: string;
  icon: ComponentProps<typeof Card>["icon"];
  /** The skeleton's height: the card's final height where it is known. */
  height: number;
  error?: AppError | null | undefined;
  onRetry: () => void;
  retrying?: boolean | undefined;
  hs: Hs;
}

/** The card while it loads or after its read failed; nothing for any other state (the card draws itself). */
export function CardReadSlot({ state, title, icon, height, error, onRetry, retrying, hs }: CardReadSlotProps) {
  if (state === "loading") return <Card title={title} icon={icon} loading={{ height }} />;
  if (state !== "failed" || !error) return null;
  return (
    <Card
      title={title}
      icon={icon}
      error={{
        message: RULE_LABELS.failedSyncRetryable,
        problem: { status: error.status, refusal_code: error.code, request_id: error.requestId },
        onRetry,
        retrying,
        hs,
      }}
    />
  );
}
